import { SEOContentFAQ } from "../../../../components/seo/SEOContentFAQ";
import { SEOImage } from "../../../../components/seo/SEOImage";

export const OratoriaVideoEN = () => (
    <div className="text-slate-300">
        <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight text-center">
            Public Speaking for Videos: 3 Pillars of Magnetic Presentation
        </h2>

        <p className="lead text-xl text-slate-300 mb-8 text-center max-w-3xl mx-auto font-medium">
            Have you ever felt that "butterflies in your stomach" before recording a video? Or lost your place in the script, diverted your gaze from the camera, and lost connection with your audience? In today's dynamic digital world, video speaking is essential, but mastering fluency and naturalness can be a huge challenge.
        </p>

        <p className="text-slate-300 mb-12 text-center max-w-3xl mx-auto leading-relaxed">
            <strong>But here's the secret: public speaking isn't a gift, it's a trained skill.</strong> Speaking well on camera involves aligning your mind, your body, and the right tools. In this guide, we'll explore the 3 pillars that transform insecure speech into a magnetic presentation, and how <strong>PromptNinja</strong> is the secret partner that lets you focus on what truly matters: your connection with the audience.
        </p>

        <SEOImage
            slug="dicas-oratoria-video"
            src="public-speaking-video-confident-youtuber.webp"
            alt="Confident YouTuber recording with a teleprompter"
            caption="Magnetic public speaking is born from security; tools like a teleprompter release your natural charisma."
            width={1200}
            height={675}
            priority={true}
        />

        <div className="bg-blue-600/10 border border-blue-500/20 rounded-2xl p-6 md:p-8 text-center mb-12 shadow-xl">
            <h3 className="text-2xl font-bold text-white mb-4">Tired of Stuttering or Forgetting Your Script?</h3>
            <p className="text-slate-300 mb-6 font-medium">
                Thousands of creators have already transformed how they appear on video.
                Try <strong>PromptNinja</strong> and discover the freedom to communicate naturally and professionally.
            </p>
            <a href="/?lang=en#app" className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-full transition-all hover:scale-105 shadow-lg shadow-blue-500/25">
                Start Your Free Journey Now!
            </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
                <h3 className="font-bold text-white text-xl mb-3">1. Eye Contact is Connection</h3>
                <p className="text-sm">
                    In a conversation, we look into each other's eyes. On video, your audience's eyes are the camera lens. The most common mistake is looking at your own image on the screen. This instantly breaks the connection. Your gaze needs to be fixed and direct at the lens.
                </p>
                <div className="bg-slate-800 p-3 rounded-md mt-4 border-l-4 border-purple-500">
                    <p className="text-sm font-semibold text-white">The Solution: Resizable Window</p>
                    <p className="text-xs text-slate-400">
                        <strong>PromptNinja's</strong> resizable window allows you to position the text exactly over your camera area (on Zoom or desktop). This keeps your eyes aligned with the lens naturally while reading.
                    </p>
                </div>
            </div>

            <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
                <h3 className="font-bold text-white text-xl mb-3">2. The End of Filler Words</h3>
                <p className="text-sm">
                    "Umm...", "like...", "you know?". These fillers appear when the brain is searching for the next word. They undermine your credibility and distract the viewer. Verbal fluency demonstrates authority on the subject.
                </p>
                <div className="bg-slate-800 p-3 rounded-md mt-4 border-l-4 border-purple-500">
                    <p className="text-sm font-semibold text-white">The Solution: Guided Script</p>
                    <p className="text-xs text-slate-400">
                        With the script scrolling smoothly on the screen, your brain doesn't need to "hunt" for words. <strong>PromptNinja</strong> ensures your message is delivered cleanly and professionally.
                    </p>
                </div>
            </div>

            <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
                <h3 className="font-bold text-white text-xl mb-3">3. The +10% Energy Rule</h3>
                <p className="text-sm">
                    The camera tends to "flatten" your energy. What seems normal in person can appear monotonous on video. Project your voice, gesture, and smile a bit more than you normally would. Think about raising your energy by 10 to 20%.
                </p>
                <div className="bg-slate-800 p-3 rounded-md mt-4 border-l-4 border-purple-500">
                    <p className="text-sm font-semibold text-white">The Solution: Focus on Performance</p>
                    <p className="text-xs text-slate-400">
                        By freeing your mind from the task of memorizing, you can focus 100% on your delivery, energy, and body language. The teleprompter takes care of the text.
                    </p>
                </div>
            </div>

            <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
                <h3 className="font-bold text-white text-xl mb-3">4. The Power of Pause</h3>
                <p className="text-sm">
                    Silence is as important as speech. Strategic pauses give the audience time to absorb information and create anticipation. Don't rush. Use the commas and periods in your script as moments to breathe and let the message shine.
                </p>
            </div>

            <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
                <h3 className="font-bold text-white text-xl mb-3">5. Open Body Language</h3>
                <p className="text-sm">
                    Crossing your arms or hunching your shoulders conveys insecurity. Keep your shoulders relaxed and open. Use your hands to emphasize important points, but avoid repetitive movements that distract. Taking up space conveys confidence.
                </p>
            </div>
        </div>

        {/* Contextual CTA */}
        <div className="bg-indigo-900/40 p-8 rounded-xl my-12 border-l-4 border-indigo-500 shadow-xl relative overflow-hidden group hover:bg-indigo-900/50 transition duration-300">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-indigo-500 rounded-full blur-3xl opacity-20 group-hover:opacity-30 transition duration-500"></div>

            <h3 className="font-bold text-2xl text-white mb-3 relative z-10">Stop Relying on Memory!</h3>
            <p className="text-slate-200 mb-6 relative z-10 max-w-2xl">
                Public speaking techniques work, but if you still feel insecure or lose your train of thought, the best tip is: <strong>Use PromptNinja</strong>. It allows you to read fluently, eliminating anxiety, blank stares, and the need to memorize scripts.
            </p>

            <a
                href="https://promptninja.solutionkit.com.br/?lang=en/#app"
                className="inline-block relative z-10 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 text-white font-bold py-3 px-8 rounded-lg shadow-lg hover:shadow-indigo-500/25 transform hover:-translate-y-1 transition duration-200"
            >
                Practice with FREE Teleprompter
            </a>
        </div>

        <div className="bg-slate-900 p-8 rounded-2xl border border-slate-800 shadow-lg mt-12">
            <h3 className="text-3xl font-bold text-white mb-6 text-center">The Silent Enemy: Cognitive Load</h3>
            <p className="text-center max-w-2xl mx-auto">
                Trying to remember a script, control the camera, think about lighting, and still appear natural is a recipe for disaster. This is called high cognitive load. It's when your brain is overloaded with multiple complex tasks.
            </p>
            <p className="text-center max-w-2xl mx-auto mt-4">
                The most effective tool to reduce this load is a teleprompter. It removes the heaviest task – memorization – allowing your mind to focus on what truly matters: <strong>connecting with your audience.</strong>
            </p>
        </div>

        <h3 className="text-3xl font-bold text-white mt-12 mb-6 text-center">How PromptNinja Revolutionizes Your Public Speaking</h3>
        <ul className="space-y-4 max-w-2xl mx-auto">
            <li className="bg-slate-800 p-4 rounded-lg border border-slate-700">
                <h3 className="font-bold text-white">Total Control at Your Fingertips</h3>
                <p className="text-sm text-slate-400">Use your smartphone as a secure remote control via QR Code. Pause, speed up, or slow down the text to match your natural speaking pace. No more sounding like a robot reading a script.</p>
            </li>
            <li className="bg-slate-800 p-4 rounded-lg border border-slate-700">
                <h3 className="font-bold text-white">Works on Any Device, Without Installation</h3>
                <p className="text-sm text-slate-400">Open PromptNinja in the browser on your PC, tablet, or phone. No downloads, no hassle. Record where and how you want.</p>
            </li>
            <li className="bg-slate-800 p-4 rounded-lg border border-slate-700">
                <h3 className="font-bold text-white">You Speak, the Text Follows (Pro Feature)</h3>
                <p className="text-sm text-slate-400">With our voice recognition, the script advances as you speak. It's the most fluid and professional teleprompter experience available.</p>
            </li>
        </ul>

        <SEOImage
            slug="dicas-oratoria-video"
            src="public-speaking-executive-video-conference.webp"
            alt="Executive using a teleprompter in a video conference"
            caption="Using a teleprompter in corporate video conferences ensures authority, clarity, and constant eye contact."
            width={1200}
            height={675}
        />

        <div className="text-center mt-12">
            <a
                href="https://promptninja.solutionkit.com.br"
                className="inline-block bg-gradient-to-r from-blue-500 to-purple-500 font-bold py-3 px-8 mx-auto rounded-full transition hover:brightness-110"
                style={{ color: 'white' }}
            >
                Unlock Your Confidence. Try PromptNinja for Free!
            </a>
            <p className="text-slate-400 mt-4 text-sm">The tool that separates amateurs from professionals.</p>
        </div>

        <div className="mt-16">
            <SEOContentFAQ
                title="Frequently Asked Questions (FAQ)"
                items={[
                    {
                        question: "Won't using a teleprompter make me look robotic?",
                        answer: "On the contrary. A good teleprompter, combined with PromptNinja's P2P remote control, allows you to adjust the speed in real-time, matching your natural speaking pace. The robotic appearance comes from trying to memorize and recite, not from reading fluently with the right tool."
                    },
                    {
                        question: "Do I need expensive equipment to use PromptNinja?",
                        answer: "No. You only need two devices with browser access: one to be the screen (like a laptop or tablet) and another to be the remote (your phone). No additional hardware is required."
                    },
                    {
                        question: "Is it hard to set up?",
                        answer: "It takes less than 30 seconds. Go to the website, paste your script, click \"start,\" and scan the QR code with your phone. It's the fastest and simplest way to have a professional teleprompter system."
                    }
                ]}
            />
        </div>
    </div>
);
